// The small generic number/text/select field controls shared by the two
// timeline mode files (CutsceneTimeline.tsx and IntroSequenceTimeline.tsx,
// which - per IntroSequenceTimeline's own doc comment - "deliberately
// mirrors CutsceneTimeline's structure"). Both files previously kept
// byte-identical copies of these three; pulled out here as a leaf module
// (no editor-internal imports, same reasoning as panelPrimitives.tsx) so
// both can share one definition instead of drifting independently.

export function FieldNumber({ label, value, min, max, step, onChange }: {
  label: string; value: number; min?: number; max?: number; step?: number; onChange: (v: number) => void;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ss-text-3)' }}>
      {label}
      <input className="ss-input" type="number" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))} style={{ width: 84, padding: '5px 8px', fontSize: 12 }} />
    </label>
  );
}

/** `suggestions` renders as a `<datalist>`: real project data (actor ids,
 * clip names) to type-ahead against, so a typo like `idle_loop` for
 * `Idle_Loop` is easy to catch at a glance instead of a silent dead
 * reference that only fails at runtime - not validated or enforced, since
 * audio cues and Logic signal names have no fixed vocabulary to check
 * against here. Omit for a plain text field (IntroSequenceTimeline's own
 * "Cue" field has no suggestion source). */
export function FieldText({ label, value, onChange, suggestions }: { label: string; value: string; onChange: (v: string) => void; suggestions?: string[] }) {
  const listId = suggestions ? `field-text-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined;
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ss-text-3)' }}>
      {label}
      <input className="ss-input" type="text" value={value} list={listId}
        onChange={(e) => onChange(e.target.value)} style={{ width: 140, padding: '5px 8px', fontSize: 12 }} />
      {listId && suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </label>
  );
}

export function FieldSelect({ label, value, options, onChange }: {
  label: string; value: string; options: [string, string][]; onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--ss-text-3)' }}>
      {label}
      <select className="ss-input" value={value} onChange={(e) => onChange(e.target.value)} style={{ padding: '5px 8px', fontSize: 12 }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
